import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface StoreItem {
  id: string;
  store_name: string;
  platform_type: string;
  country: string | null;
  base_currency: string | null;
  status: string;
  workspace_id: string | null;
  store_url: string | null;
}

interface StoreContextType {
  stores: StoreItem[];
  activeStoreId: string | null;
  activeStore: StoreItem | null;
  setActiveStoreId: (id: string | null) => void;
  isAllStores: boolean;
  isLoading: boolean;
}

const StoreContext = createContext<StoreContextType>({
  stores: [],
  activeStoreId: null,
  activeStore: null,
  setActiveStoreId: () => {},
  isAllStores: true,
  isLoading: true,
});

export function StoreProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [activeStoreId, setActiveStoreId] = useState<string | null>(null);

  const { data: stores = [], isLoading } = useQuery({
    queryKey: ["user-stores"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("stores")
        .select("id, store_name, platform_type, base_currency, status, store_url")
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data || []).map((s: any) => ({
        ...s,
        country: s.country || null,
        workspace_id: s.workspace_id || null,
      })) as StoreItem[];
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const activeStore = stores.find((s) => s.id === activeStoreId) || null;
  const isAllStores = activeStoreId === null;

  return (
    <StoreContext.Provider value={{ stores, activeStoreId, activeStore, setActiveStoreId, isAllStores, isLoading }}>
      {children}
    </StoreContext.Provider>
  );
}

export function useStoreContext() {
  return useContext(StoreContext);
}
